import { F } from '@/utils/intl';
import React, { useEffect, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import get from 'lodash/get';
import omit from 'lodash/omit';
import moment from 'moment';
import dayjs from 'dayjs';
import { Button, Col, Divider, Row, Space, Typography } from 'antd';
import * as PromotionSelectors from '@/selectors/promotionSelectors';

import { PromotionModel } from '@/typings/models/Promotion';
import PromotionName from '@/components/Promotion/PromotionName';
import Drawer from '@/components/Drawer';
import PromotionDetailsForm from '@/components/PromotionDrawer/PromotionDetailsForm';
import PromotionTypeForm from '@/components/PromotionDrawer/PromotionTypeForm';
import DrawerSection from '@/components/Drawer/DrawerSection';
import PromotionConditionSelector from '@/components/PromotionDrawer/PromotionConditionSelector';

import styles from './index.less';
import { processHtml, processHtmlStyles } from './utils';
import PromotionConditionDrawer from '@/components/PromotionDrawer/PromotionConditionDrawer';
import { DYNAMIC_FORM_RULE_TYPES, extractDynamicFormRules } from '@/utils/form';

const PromotionDrawer: React.FC = () => {
  const [promotionFormData, setPromotionFormData] = useState<PromotionModel>({});
  const [processedHtml, setProcessedHtml] = useState('');

  const dispatch = useDispatch();
  const promotionTypes = useSelector(PromotionSelectors.selectPromotionTypes);

  const visible = useSelector(PromotionSelectors.selectPromotionDrawerVisible);
  const drawer = useSelector(PromotionSelectors.selectPromotionDrawer);

  const promotion = useSelector(
    PromotionSelectors.selectPromotionDrawerPromotion,
  );
  const createPromotionLoading = useSelector(
    PromotionSelectors.selectPromotionLoading('create'),
  );
  const editPromotionLoading = useSelector(
    PromotionSelectors.selectPromotionLoading('edit'),
  );

  const selectedJourneyConfig = useSelector(
    (state: DefaultRootState) => state.journey?.config?.[state.journey.selectedJourney?.hashId],
  );


  useEffect(() => {
    if (visible) {
      if (promotion?.id === 'NEW') {
        setPromotionFormData({});
        setProcessedHtml('');
      } else if (promotion?.id && promotion.id !== 'NEW') {
        // Initialize form data with existing promotion when editing
        const initialFormData: any = {
          title: promotion.title,
          is_active: promotion.is_active,
          range: [
            promotion.start_date ? dayjs(promotion.start_date) : null,
            promotion.end_date ? dayjs(promotion.end_date) : null,
          ],
          promo_type: promotion.promo_type_id && promotion.promo
            ? {
              id: promotion.promo_type_id,
              ...promotion.promo,
            }
            : undefined,
          condition_id: promotion.condition_id,
        };
        setPromotionFormData(initialFormData);
      }
    } else {
      setPromotionFormData({});
      setProcessedHtml('');
    }
  }, [visible, promotion?.id]);

  useEffect(() => {
    if (promotionFormData?.promo_type)
      setProcessedHtml(
        processHtmlStyles(
          processHtml(
            promotion?.promo_type?.preview ||
            promotionTypes.find((x) => x.id === promotionFormData?.promo_type?.id)?.preview,
            promotionFormData?.promo_type,
          ),
          selectedJourneyConfig,
        ),
      );
  }, [JSON.stringify(promotionFormData)]);

  function toggle() {
    dispatch({
      type: 'promotion/toggleDrawer',
      payload: {
        promotion: null,
      },
    });
    dispatch({
      type: 'promotion/resetRecentlyCreatedPromotionCondition',
    });
    setPromotionFormData({});
  }

  function isNewPromotion() {
    return promotion.id === 'NEW';
  }

  //We send empty url if its validation included CUSTOM_URL_EMPTY_IF_DEFAULT
  const clearUrlIfDefault = (payload) => {
    const selectedPromoType = promotionTypes?.find(type => type.id === payload?.promo_type_id)?.fields?.find(field => field.key === 'url');
    const rules = extractDynamicFormRules(selectedPromoType?.validation);
    if (!rules[DYNAMIC_FORM_RULE_TYPES.CUSTOM_URL_EMPTY_IF_DEFAULT]) return;
    const selectedPromoTypeURLField = promotionTypes?.find(type => type.id === payload?.promo_type_id)?.fields?.find(field => field.key === 'url');
    const defaultUrl = selectedPromoTypeURLField?.['default'];
    if (payload?.promo?.url === defaultUrl) {
      payload.promo.url = '';
    }
  };
  const getPromotionConditionId = () =>
    promotionFormData.condition_id || promotion?.condition_id;

  function preparePayload() {
    // Use form data if available, otherwise fall back to original promotion data
    // Check for both undefined/null and empty string to ensure we always have values
    const title = (promotionFormData.title !== undefined && promotionFormData.title !== null && promotionFormData.title !== '')
      ? promotionFormData.title
      : promotion?.title;
    const is_active = promotionFormData.is_active !== undefined && promotionFormData.is_active !== null
      ? promotionFormData.is_active
      : promotion?.is_active;

    // Check if range exists and has valid values
    const formDataAny = promotionFormData as any;
    const range = (formDataAny.range && Array.isArray(formDataAny.range) && formDataAny.range[0] && formDataAny.range[1])
      ? formDataAny.range
      : (promotion?.start_date && promotion?.end_date ? [
        dayjs(promotion.start_date),
        dayjs(promotion.end_date),
      ] : null);

    const promo_type = promotionFormData.promo_type ?? (promotion?.promo_type_id && promotion?.promo ? {
      id: promotion.promo_type_id,
      ...promotion.promo,
    } : undefined);

    const payload: any = {
      title,
      is_active,
    };
    payload.promo_type_id = get(promo_type, 'id') || promotion?.promo_type_id;
    payload.promo = omit(promo_type || {}, ['id', 'validated']);
    clearUrlIfDefault(payload);

    if (range && range[0] && range[1]) {
      payload.start_date = moment(new Date(range[0])).format('YYYY-MM-DD');
      payload.end_date = moment(new Date(range[1])).format('YYYY-MM-DD');
    } else if (promotion?.start_date && promotion?.end_date) {
      // Fallback to original promotion dates if range is not available
      payload.start_date = promotion.start_date;
      payload.end_date = promotion.end_date;
    }

    payload.condition_id = getPromotionConditionId();
    return payload;
  }

  function onCreatePromotion() {
    dispatch({
      type: 'promotion/create',
      payload: preparePayload(),
    });
  }

  function onEditPromotion() {
    dispatch({
      type: 'promotion/edit',
      payload: {
        ...preparePayload(),
        emailPromotionId: promotion.id,
      },
    });
  }

  function getFooter() {
    return (
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button
            className={'button-secondary button-bordered'}
            size={'large'}
            type={'primary'}
            onClick={toggle}
          >
            {isNewPromotion() ? (
              <F id={'common.actions.close'} defaultMessage={'Close'} />
            ) : (
              <F id={'common.actions.discard'} defaultMessage={'Discard'} />
            )}
          </Button>
          {isNewPromotion() ? (
            <Button
              size={'large'}
              loading={createPromotionLoading}
              onClick={onCreatePromotion}
              type={'primary'}
            >
              <F id={'pages.promotions.addPromotion'} defaultMessage={'Add Promotion'} />
            </Button>
          ) : (
            <Button
              size={'large'}
              loading={editPromotionLoading}
              onClick={onEditPromotion}
              type={'primary'}
            >
              <F id={'common.actions.save'} defaultMessage={'save'} />
            </Button>
          )}
        </Space>
      </div>
    );
  }

  function getTitle() {
    return isNewPromotion() ? (
      intl.formatMessage({
        id: 'pages.promotions.newPromotion',
        defaultMessage: 'New Promotion',
      })
    ) : (
      <PromotionName promotion={promotion} drawer={false} />
    );
  }

  const intl = useIntl();

  return (
    <div className={styles.drawerContainer}>
      <Drawer
        width={'70%'}
        footer={getFooter()}
        title={getTitle()}
        open={visible}
        destroyOnHidden
        onOpenChange={toggle}
      >
        <Row gutter={16} style={{ flex: 1 }}>
          <Col span={10}>
            <Divider plain>
              <Typography.Title level={3}> <F id={'pages.promotions.form'} defaultMessage={'Form'} /></Typography.Title>
            </Divider>
            <PromotionDetailsForm
              onChange={(details) => setPromotionFormData((prev) => ({ ...prev, ...details }))}
              promotion={promotion}
              isNew={isNewPromotion()}
            />
            <DrawerSection
              title={intl.formatMessage({
                id: 'pages.promotions.type',
                defaultMessage: 'Promotion type',
              })}
            >
              <PromotionTypeForm
                onChange={(promotionType) =>
                  setPromotionFormData((prev: any) => ({ ...prev, promo_type: promotionType }))
                }
                promotion={promotion}
                isNew={isNewPromotion()}
              />
            </DrawerSection>
            <DrawerSection
              title={intl.formatMessage({
                id: 'pages.promotions.promotionCondition',
                defaultMessage: 'Terms and conditions',
              })}
            >
              <PromotionConditionSelector value={getPromotionConditionId()}
                                          onChange={(promotionConditionId) => setPromotionFormData((prev) => ({
                                            ...prev,
                                            condition_id: promotionConditionId,
                                          }))} />
            </DrawerSection>
          </Col>

          <Col offset={2} span={12}>
            <Divider plain>
              <Typography.Title level={3}> <F id={'pages.promotions.preview'}
                                              defaultMessage={'Preview'} /></Typography.Title>
            </Divider>
            <div
              style={{
                border: '1px solid gray',
                borderRadius: 10,
                padding: 20,
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
            </div>
          </Col>
        </Row>
        {visible && <PromotionConditionDrawer />}
      </Drawer>
    </div>
  );
};

export default PromotionDrawer;
